/**
 * Orientation details editor (modal). Edits display name / occupation target /
 * description and the lifecycle status. Used for both "New orientation" and
 * editing an existing one. Commits via the parent's onSave; status changes route
 * to set_orientation_status separately (status is not part of update_orientation).
 */
import { useState } from 'react';
import { STATUS_DESC, STATUS_TAGS, passingScoreLabel, type StatusTag } from './model';
import { SearchSelect } from './SearchSelect';
import { GatedButton } from '../GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';

// The "Default version" dropdown is gone: single-length mode always saves
// `default_version = Default` (see model.ts EDIT_VERSION).
export type OrientationDraft = {
  displayName: string;
  occupationTarget: string;
  description: string;
  autoAssign: boolean;
  status: StatusTag;
  /** Check settings (set_orientation_check_settings). The score is stored as a
   *  percent so it survives checks being added or removed; null = no pass mark. */
  passingScorePct: number | null;
  showAnswersAtEnd: boolean;
  autoReassignOnFail: boolean;
  maxAttempts: number | null;
};

type Props = {
  title: string;
  initial: OrientationDraft;
  /** Hide status when creating (a new orientation is always Draft). */
  showStatus: boolean;
  /** How many checks this orientation currently has, for the derived
   *  "N/M correct" the passing score always shows alongside the percent. */
  checkCount: number;
  /** Who created the orientation (read-only); omitted when creating. */
  createdBy?: string;
  onCancel: () => void;
  onSave: (draft: OrientationDraft) => void;
};

export function OrientationDetails({ title, initial, showStatus, checkCount, createdBy, onCancel, onSave }: Props) {
  const [d, setD] = useState<OrientationDraft>(initial);
  const patch = (p: Partial<OrientationDraft>) => setD((prev) => ({ ...prev, ...p }));

  return (
    <div className="oe-modal-bd" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="oe-modal">
        <div className="oe-modal-head">
          <h2>{title}</h2>
          <button className="oe-btn ghost sm" aria-label="Close" onClick={onCancel}><Icon of={Icons.action.remove} size={14} /></button>
        </div>
        <div className="oe-modal-body">
          {createdBy !== undefined && (
            <div className="oe-field">
              <label>Created by</label>
              <span>{createdBy || 'Unknown (predates author tracking)'}</span>
            </div>
          )}
          <div className="oe-field">
            <label>Display name</label>
            <input className="oe-inp" value={d.displayName} onChange={(e) => patch({ displayName: e.target.value })} />
          </div>
          <div className="oe-field">
            <label>Occupation target (comma-separated; blank = universal)</label>
            <input className="oe-inp" value={d.occupationTarget} onChange={(e) => patch({ occupationTarget: e.target.value })} />
          </div>
          <div className="oe-field">
            <label>Description</label>
            <textarea className="oe-inp" value={d.description} onChange={(e) => patch({ description: e.target.value })} />
          </div>
          <div className="oe-field">
            <label>Auto-assign</label>
            <SearchSelect
              value={d.autoAssign ? 'yes' : 'no'}
              options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
              onChange={(v) => patch({ autoAssign: v === 'yes' })}
            />
            <div className="oe-hint" style={{ margin: '4px 0 0' }}>
              Yes: users are enrolled automatically when they join the site, if their
              occupation is in the target list above (a blank target enrolls everyone).
              Only Active orientations are auto-assigned.
            </div>
          </div>
          {showStatus && (
            <div className="oe-field">
              <label>Status — Draft hides from pickers, Active publishes, Archived soft-removes</label>
              <SearchSelect
                value={d.status}
                options={STATUS_TAGS.map((s) => ({ value: s, label: s, title: STATUS_DESC[s] }))}
                onChange={(v) => patch({ status: v as StatusTag })}
              />
            </div>
          )}

          <section className="oe-sect checks" aria-labelledby="oe-checks-title">
          <div className="oe-sect-head">
            <div className="oe-sect-title" id="oe-checks-title">Comprehension checks</div>
            <div className="oe-hint" style={{ margin: 0 }}>
              Orientation-wide rules for scoring and re-assignment. The questions themselves,
              and their retry and time limits, are authored per check in the timeline.
            </div>
          </div>
          <div className="oe-field">
            <label title="Share of this orientation's checks that must be answered correctly. Off = no pass mark, checks are informational. Stored as a percent so it survives checks being added or removed; the question count it works out to is shown beside it.">
              Passing score (%)
              <button
                className={'oe-tgl sm' + (d.passingScorePct != null ? ' on' : '')}
                type="button"
                aria-pressed={d.passingScorePct != null}
                aria-label={`Passing score: ${d.passingScorePct != null ? 'on' : 'off'}`}
                onClick={() => patch({ passingScorePct: d.passingScorePct == null ? 80 : null })}
              />
            </label>
            <input
              className="oe-inp mono"
              type="number"
              min={0}
              max={100}
              value={d.passingScorePct ?? ''}
              placeholder="no pass mark"
              disabled={d.passingScorePct == null}
              title="Percent of this orientation's checks the trainee must get right"
              onChange={(e) => patch({
                passingScorePct: e.target.value === ''
                  ? null
                  : Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)),
              })}
            />
            <div className="oe-hint" style={{ margin: '4px 0 0' }}>
              {passingScoreLabel(d.passingScorePct, checkCount)}
              {checkCount === 0 && d.passingScorePct != null ? ' (no checks authored yet)' : ''}
            </div>
          </div>

          <div className="oe-field">
            <label title="At the end of the orientation, show the trainee each question with their answer next to the correct one.">Show answers at end</label>
            <SearchSelect
              value={d.showAnswersAtEnd ? 'yes' : 'no'}
              options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
              onChange={(v) => patch({ showAnswersAtEnd: v === 'yes' })}
            />
          </div>

          <div className="oe-field">
            <label title="When the final score is below the passing score, open a fresh attempt automatically instead of leaving it failed for a manager.">Auto re-assign on fail</label>
            <SearchSelect
              value={d.autoReassignOnFail ? 'yes' : 'no'}
              options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
              onChange={(v) => patch({ autoReassignOnFail: v === 'yes' })}
            />
          </div>

          <div className="oe-field">
            <label title="How many attempts a trainee gets before a failure is left for a human in the review queue. Only meaningful while auto re-assign is on; off = no cap.">
              Max attempts
              <GatedButton
                className={'oe-tgl sm' + (d.maxAttempts != null ? ' on' : '')}
                aria-pressed={d.maxAttempts != null}
                aria-label={`Max attempts: ${d.maxAttempts != null ? 'on' : 'off'}`}
                reason={d.autoReassignOnFail ? null : 'Turn on auto re-assign first'}
                onClick={() => patch({ maxAttempts: d.maxAttempts == null ? 3 : null })}
              />
            </label>
            <input
              className="oe-inp mono"
              type="number"
              min={1}
              value={d.maxAttempts ?? ''}
              placeholder={d.autoReassignOnFail ? 'no cap' : 'auto re-assign is off'}
              disabled={!d.autoReassignOnFail || d.maxAttempts == null}
              title="Attempts allowed before the assignment is flagged failed for a human"
              onChange={(e) => patch({
                maxAttempts: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1),
              })}
            />
          </div>
          </section>
        </div>
        <div className="oe-modal-foot">
          <button className="oe-btn" onClick={onCancel}>Cancel</button>
          <GatedButton className="oe-btn primary" onClick={() => onSave(d)} reason={d.displayName.trim() ? null : 'Give the orientation a name first'}>Save</GatedButton>
        </div>
      </div>
    </div>
  );
}
